import $ from 'jquery';
import * as ArbitraryEvents from '../arbitrary_events';
import 'js/utils/$a_global';

type EnabledFeatures = {
  listenForHashChange?: boolean;
  listenForClicks?: boolean;
};

type EmptyObject = Record<string, never>;

// NotifyCollectionMap contains the type mappings from the collectionName to
// the eventData.  It is not required to define all the possible mappings, but
// it's nice to have them here to improve developers' sanity.
interface NotifyCollectionMap {
  continue_reading_carousel: {
    event: string;
    pathname: string;
    href: string;
    source: string;
  };
  upload_funnel_pages: {
    page: string;
    pageNum: number;
    sawClaimPapersFirstStep: boolean;
    allowMentionsUpsell: boolean;
    showSocialUploadPath: boolean;
    trigger: string;
    premium: boolean;
  };
  'sutd_cta_above_fold:false': EmptyObject;
  'sutd_cta_above_fold:true': EmptyObject;
  [others: string]: unknown;
}
type CollectionName = Extract<keyof NotifyCollectionMap, string>;

export const Kale = (function () {
  let enabledFeatures: EnabledFeatures;
  let loggerContext = $a as any;

  // Generate 8 character long random string to group all sequenceIds for this page interaction.
  let pageKey = '';
  for (let i = 0; i < 8; i++) {
    let character = Math.floor(Math.random() * 24);
    if (Math.random() < 0.5) {
      // uppercase
      character += 65;
    } else {
      // lowercase
      character += 97;
    }
    pageKey += String.fromCharCode(character);
  }
  // sequenceId increments on each request to give a canonical order to the events.
  let sequenceId = 0;

  /* options:
   *   listenForClicks
   *   listenForHashChange
   */
  function enableFeatures(options: EnabledFeatures) {
    if (options.listenForClicks === true) listenForClicks();
    if (options.listenForHashChange === true) listenForHashChange();
  }

  /* options:
   *   collectionName: name of collection for the event to be stored in
   *   eventData: optional hash of data for event
   */
  function notify<K extends CollectionName>(options: {
    collectionName: K;
    eventData: NotifyCollectionMap[K];
  }) {
    options = options || {};
    if (!options.eventData) {
      loggerContext.error('[Kale] Must pass in data to notify');
      return;
    }

    notifyEvent({
      collectionName: options.collectionName,
      eventData: options.eventData,
    });
  }

  function setLogger(func: {
    log: (data: string) => void;
    error: (data: string) => void;
  }) {
    loggerContext = func;
  }

  // Hash Change

  function listenForHashChange() {
    enabledFeatures = enabledFeatures || {};
    if (!enabledFeatures.listenForHashChange) {
      enabledFeatures.listenForHashChange = true;
      $(window).on('hashchange', notifyPageViewed);
      loggerContext.log('[Kale] listenForHashChange enabled');
    }
  }

  function notifyPageViewed() {
    notifyEvent({
      collectionName: 'kale_page_views',
      eventData: { url: window.location.toString() },
    });
  }

  // Clicks

  function listenForClicks() {
    enabledFeatures = enabledFeatures || {};
    if (!enabledFeatures.listenForClicks) {
      enabledFeatures.listenForClicks = true;
      $(document).on(
        'click',
        '[data-kale-component]',
        function (e: $.ClickEvent) {
          e.preventDefault();
          notifyClick({ domElement: e.currentTarget });
          if (e.currentTarget.href) {
            if (
              e.metaKey ||
              e.ctrlKey ||
              e.shiftKey ||
              e.currentTarget.target === '_blank'
            ) {
              window.open(e.currentTarget.href, '_blank');
            } else {
              window.location = e.currentTarget.href;
            }
          }
        },
      );
      loggerContext.log('[Kale] listenForClicks enabled');
    }
  }

  function notifyClick(options: { domElement: EventTarget | null }) {
    options = options || {};

    const $domElement = $(options.domElement);
    if ($domElement.length === 0) {
      loggerContext.error(
        '[Kale] domElement does not exist',
        options.domElement,
      );
      return;
    }

    const componentName = $domElement.attr('data-kale-component');
    if (!componentName) {
      loggerContext.error(
        '[Kale] Must set a non-empty component name for data-kale-component',
      );
      return;
    }

    const collectionName = $domElement.attr('data-kale-collection');
    if (!collectionName) {
      loggerContext.error(
        '[Kale] Must set a collection for data-kale-collection',
      );
      return;
    }

    let componentData = $domElement.attr('data-kale-data') || '{}';
    try {
      componentData = JSON.parse(componentData);
    } catch {
      loggerContext.error('[Kale] Unable to parse JSON from data-kale-data');
    }

    notifyEvent({
      collectionName: collectionName,
      componentName: componentName,
      componentAction: 'clicked',
      eventData: componentData,
    });
  }

  /* options:
   *   collectionName: name of collection for the event to be stored in
   *   eventData: optional hash of data for event
   */
  function notifyEvent<K extends CollectionName>(options: {
    collectionName: K;
    componentName?: string;
    componentAction?: string;
    eventData: NotifyCollectionMap[K];
  }) {
    options = options || {};
    ArbitraryEvents.write('KaleEvent', {
      collection_name: options.collectionName,
      page_key: pageKey,
      sequence_id: sequenceId,
      component_name: options.componentName,
      component_action: options.componentAction,
      event_data: JSON.stringify(options.eventData),
    });
    sequenceId++;

    loggerContext.log(
      '[Kale Event]:',
      options.collectionName,
      options.eventData,
    );
  }

  // enable by default
  listenForClicks();

  return {
    enableFeatures: enableFeatures,
    notify: notify,
    // This is a low-level function only used for broccoli compatibility
    notifyEvent: notifyEvent,
    setLogger: setLogger,
  };
})();

export default Kale;
